/**
 * Gunner — the RUN-AND-GUN ground shooter (step 3). The first RANGED enemy.
 *
 * A ground grunt (gravity + optional slow patrol, reusing the Walker spine) that FACES the
 * player and — when the player is in range at roughly its own height, on its facing side — fires
 * an ENEMY projectile after a short telegraph (the `_fire` pose shows during wind-up so the shot
 * is dodgeable). Otherwise a normal killable grunt: stomp-from-above kills+bounces, a bullet
 * kills it, any other contact hurts.
 *
 * Determinism: integer step counter (sinceFire) + abs/min/max + player-position compares; the
 * bullet emits through ctx.spawnEntityAt's deterministic end-of-step queue. No transcendentals /
 * random / wall-clock → ESLint-clean under src/engine/. A replay re-fires identically.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';
import { PROJECTILE_H } from './Projectile';

const GRAVITY = 2050;
const TERMINAL = 1200;
const SQUASH_SECONDS = 0.4;
const DEFAULT_SPEED = 55;       // patrol speed (px/s) — soldiers march by default (0 = emplaced)
const DEFAULT_COOLDOWN = 110;   // steps between shots (~1.8s @60)
const DEFAULT_TELEGRAPH = 16;   // wind-up steps (fire pose) before the shot (~0.27s)
const DEFAULT_BULLET_SPEED = 340;
const DEFAULT_BULLET_LIFE = 160;
const DEFAULT_RANGE_PX = 720;   // horizontal firing range
const V_RANGE_PX = 150;         // vertical tolerance (won't shoot a player far above/below)

export class Gunner implements Entity {
  readonly type = EntityType.GUNNER;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = -1;
  readonly spriteFacesLeft = true; // Kenney enemy art faces left
  alive = true;
  squashTimer = 0;

  private speed: number;          // 0 = stationary (default); >0 = patrol
  private turnAtLedge: boolean;
  private cooldown: number;
  private telegraph: number;
  private bulletSpeed: number;
  private bulletLife: number;
  private rangePx: number;
  private sinceFire = 0;
  private firePose = false;
  private walkPhase = 0;

  constructor(cx: number, feetY: number, props?: Record<string, unknown>, w = 48, h = 52) {
    this.body = { x: cx - w / 2, y: feetY - h, w, h };
    this.speed = typeof props?.speed === 'number' ? props.speed : DEFAULT_SPEED;
    this.turnAtLedge = props?.turnAtLedge !== false;
    this.cooldown = typeof props?.fireCooldownSteps === 'number'
      ? Math.max(20, Math.floor(props.fireCooldownSteps)) : DEFAULT_COOLDOWN;
    this.telegraph = typeof props?.telegraphSteps === 'number'
      ? Math.max(1, Math.floor(props.telegraphSteps)) : DEFAULT_TELEGRAPH;
    this.bulletSpeed = typeof props?.bulletSpeed === 'number' ? props.bulletSpeed : DEFAULT_BULLET_SPEED;
    this.bulletLife = typeof props?.bulletLifeSteps === 'number'
      ? Math.floor(props.bulletLifeSteps) : DEFAULT_BULLET_LIFE;
    this.rangePx = typeof props?.rangePx === 'number' ? props.rangePx : DEFAULT_RANGE_PX;

    const dir = props?.dir;
    if (dir === 1 || dir === '1' || dir === 'right') this.facing = 1;
    else if (dir === -1 || dir === '-1' || dir === 'left') this.facing = -1;
  }

  update(dt: number, ctx: EntityContext): void {
    if (!this.alive) { this.squashTimer = Math.max(0, this.squashTimer - dt); return; }
    const grid = ctx.grid;

    // gravity + ground
    this.vy = Math.min(this.vy + GRAVITY * dt, TERMINAL);
    if (grid.collideY(this.body, this.vy * dt, false).grounded) this.vy = 0;

    // optional slow patrol (reverse at wall / ledge)
    if (this.speed > 0) {
      this.vx = this.facing * this.speed;
      if (grid.collideX(this.body, this.vx * dt)) this.reverse();
      if (this.turnAtLedge) {
        const aheadX = this.facing > 0 ? this.body.x + this.body.w + 2 : this.body.x - 2;
        const belowY = this.body.y + this.body.h + 4;
        if (!grid.solidBelowPoint(aheadX, belowY)) this.reverse();
      }
    }

    // aim: a stationary gunner turns to face the player (so a horizontal shot goes at them)
    const pcx = ctx.player.body.x + ctx.player.body.w / 2;
    const scx = this.body.x + this.body.w / 2;
    if (this.speed === 0) this.facing = pcx < scx ? -1 : 1;

    // fire when the player is in range at roughly this height, on the facing side
    const dx = Math.abs(pcx - scx);
    const dyFeet = Math.abs((ctx.player.body.y + ctx.player.body.h) - (this.body.y + this.body.h));
    const onFacingSide = (pcx < scx) === (this.facing < 0);
    const inRange = dx <= this.rangePx && dyFeet <= V_RANGE_PX && onFacingSide;

    if (inRange) {
      this.sinceFire++;
      this.firePose = this.sinceFire >= this.cooldown - this.telegraph;
      if (this.sinceFire >= this.cooldown) { this.fire(ctx); this.sinceFire = 0; this.firePose = false; }
    } else {
      this.sinceFire = 0;
      this.firePose = false;
    }

    this.walkPhase += Math.abs(this.vx) * dt; // drives the 2-frame walk cycle (0 when emplaced)
  }

  private fire(ctx: EntityContext): void {
    const muzzleCx = this.body.x + this.body.w / 2 + this.facing * (this.body.w * 0.5 + 4);
    const muzzleCy = this.body.y + this.body.h * 0.42;
    ctx.spawnEntityAt(EntityType.PROJECTILE, muzzleCx, muzzleCy + PROJECTILE_H / 2, {
      vx: this.facing * this.bulletSpeed, vy: 0, life: this.bulletLife, owner: 'enemy',
    });
    ctx.playSound('shoot', 0.3, 0.6); // POLISH_01 #1 — enemy blaster (pitched below the player's)
  }

  onPlayerOverlap(ctx: EntityContext): void {
    const p = ctx.player.body;
    const playerFeet = p.y + p.h;
    const fallingOnto = ctx.player.vy > 0 && playerFeet - this.body.y < this.body.h * 0.7;
    if (fallingOnto && ctx.offenseEnabled) {
      ctx.killEntity(this);
      ctx.bouncePlayer();
      ctx.playSound('stomp', 0.5); // POLISH_01 #1 — dedicated stomp clip
    } else {
      ctx.hurtPlayer();
    }
  }

  onKilled(): void {
    if (!this.alive && this.squashTimer > 0) return;
    this.alive = false;
    this.squashTimer = SQUASH_SECONDS;
    this.vx = 0;
  }

  damageable(): boolean { return true; } // stompable + shootable like a GOOMBA

  private get walkFrameA(): boolean { return Math.floor(this.walkPhase / 40) % 2 === 0; }

  spriteKey(): string {
    if (!this.alive) return 'gunner_rest'; // no bespoke flat pose yet → reuse rest for the squash beat
    if (this.firePose) return 'gunner_fire';
    if (Math.abs(this.vx) > 1) return this.walkFrameA ? 'gunner_walk_1' : 'gunner_walk_2';
    return 'gunner_rest';
  }

  visible(): boolean { return this.alive || this.squashTimer > 0; }

  private reverse(): void {
    this.facing = this.facing > 0 ? -1 : 1;
  }
}
